import React from 'react';
import { isEmpty, isNil } from 'lodash/fp';
import { style } from 'typestyle';
import StarRatings from 'react-star-ratings';
import { ConfigurableGridLinkButton } from 'configuration-schemas/viewdefns/general';
import Adornments from 'src/components/Adornments/Adornments';
import CenteredImage from 'src/components/CenteredImage/CenteredImage';
import Renderer from 'src/utils/Domain/Renderer';
import { decorateTextByClasses } from 'src/style/styles.utils';
import { starGoldColor } from 'src/utils/Style/Theme';
import {
  starDimension,
  starSpacing,
  HORPADDING,
  BORDERWIDTH,
  CARD_FONT_FAMILY,
} from '../StandardCard/StandardCard.styles';
import collCardStyles, { COLLCARD_WIDTH, COLLCARD_HEIGHT, ITEM_THUMB_SIZE } from './CollectionCard.styles';
import { Props, executeItemClick, buildContextOptions, executeLinkButtonClick } from './CollectionCard';

const LIGHT_IMG_W = 190;
const LIGHT_IMG_H = 233;

const lightCard = style({
  $debugName: 'collectionCardLight-card',
  height: '88%',
  width: '100%',
  display: 'inline-flex',
  flexDirection: 'column',
  position: 'relative',
  background: 'white',
  border: '1px solid #e0e0e0',
  borderRadius: 8,
  fontFamily: CARD_FONT_FAMILY,
  breakInside: 'avoid',
  pageBreakInside: 'avoid',
});

const lightHeader = style({
  $debugName: 'collectionCardLight-header',
  padding: '10px 14px 0',
  backgroundColor: '#efefef',
});

const lightTopRow = style({
  $debugName: 'collectionCardLight-topRow',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  $nest: {
    '': {
      marginLeft: 'auto',
    },
  },
});

const lightStyleId = style({
  $debugName: 'collectionCardLight-styleId',
  fontSize: '0.7rem',
  color: '#888',
  flex: 1,
});

const lightName = style({
  $debugName: 'collectionCardLight-name',
  fontWeight: 700,
  fontSize: '1.1rem',
  margin: '0 0 4px',
});

const lightDesc = style({
  $debugName: 'collectionCardLight-desc',
  fontSize: '0.75rem',
  color: '#888888',
  fontStyle: 'italic',
  margin: '0 0 8px',
  paddingBottom: '45px',
  overflow: 'hidden',
  display: '-webkit-box',
  maxHeight: '40px',
  minHeight: '40px',
  '-webkit-line-clamp': 2,
  // @ts-ignore
  '-webkit-box-orient': 'vertical',
});

const lightSelectedIcon = style({
  $debugName: 'collectionCardLight-selectedIcon',
  fontSize: '1.4rem',
  color: '#00bda5',
  lineHeight: 1,
});

const lightBody = style({
  $debugName: 'collectionCardLight-body',
  display: 'flex',
  flexDirection: 'row',
  padding: '10px 14px',
  gap: 16,
  flex: 1,
  minHeight: 0,
  alignItems: 'center',
});

const lightAnchorSection = style({
  $debugName: 'collectionCardLight-anchorSection',
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
  $nest: {
    div: {
      textAlign: 'left',
    },
  },
});

const lightAnchorFooter = style({
  $debugName: 'collectionCardLight-anchorFooter',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginTop: 4,
});

const lightAnchorValue = style({
  $debugName: 'collectionCardLight-anchorValue',
  fontSize: '0.85rem',
  fontWeight: 700,
  color: '#171717',
});

const lightAnchorCaption = style({
  $debugName: 'collectionCardLight-anchorCaption',
  display: 'flex',
  flexDirection: 'column',
  marginTop: 2,
  padding: 3,
});

const lightAnchorCaptionId = style({
  $debugName: 'collectionCardLight-anchorCaptionId',
  fontSize: '0.65rem',
  color: '#888',
});

const lightAnchorCaptionName = style({
  $debugName: 'collectionCardLight-anchorCaptionName',
  fontSize: '0.6rem',
  color: '#171717',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: LIGHT_IMG_W,
});

const lightStats = style({
  $debugName: 'collectionCardLight-stats',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  gap: 3,
  paddingTop: 4,
  maxHeight: '130px',
  overflowY: 'auto',
});

const lightStatRow = style({
  $debugName: 'collectionCardLight-statRow',
  display: 'flex',
  flexDirection: 'row',
  gap: 6,
  fontSize: '0.75rem',
});

const lightStatLabel = style({
  $debugName: 'collectionCardLight-statLabel',
  fontWeight: 700,
  color: '#171717',
  minWidth: 110,
});

const lightStatValue = style({
  $debugName: 'collectionCardLight-statValue',
  color: '#171717',
});

const lightItemsRow = style({
  $debugName: 'collectionCard-itemsRow',
  display: 'flex',
  flexDirection: 'row',
  gap: 8,
  flexWrap: 'nowrap',
  overflowX: 'auto',
  maxWidth: '260px',
  marginTop: '28px',
});
const lightSectionDetails = style({
  $debugName: 'collectionCardLight-sectionDetails',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
});
export function CollectionCardLight(props: Props) {
  const {
    id,
    name,
    description,
    styleDescription,
    styleId,
    isSelected,
    adornments,
    imgSrc,
    children,
    buttons,
    columns,
    stars,
    value,
    valueRenderer,
    valueRendererParams,
  } = props;

  const renderAdornments = !isEmpty(adornments);
  const contextOptions = buildContextOptions(props);
  const linkButtons = (buttons ?? []).filter((btn): btn is ConfigurableGridLinkButton => btn.type === 'link');

  const rendererFn = (Renderer as unknown as Record<string, (v: unknown, mask?: string) => unknown>)[valueRenderer];
  const renderedValue = rendererFn ? rendererFn(value, valueRendererParams?.mask) : value;

  return (
    <div
      className={lightCard}
      data-qa-component="CollectionCardLight"
      data-qa-action="OpenPopover"
      data-qa-key={id}
      onClick={() => executeItemClick(props)}
    >
      {isSelected && (
        <React.Fragment>
          <div className={collCardStyles.cardOverlay} />
          <i className={`fas fa-check-circle ${collCardStyles.iconOverlay}`} />
        </React.Fragment>
      )}
      {/* Header */}
      <div className={lightHeader}>
        <div className={lightTopRow}>
          <div style={{ marginRight: 'auto' }}>
            <span className={lightStyleId}>{styleId}</span>
            <div className={lightName}>{name}</div>
          </div>

          {renderAdornments && <Adornments adornments={adornments} productId={id} contextOptions={contextOptions} />}
          {linkButtons.map((btn, i) => (
            <div key={i} className={collCardStyles.linkButton} onClick={(e) => executeLinkButtonClick(props, btn, e)}>
              {btn.icon && <i className={btn.icon} style={{ marginRight: 4 }} />}
              {btn.text}
            </div>
          ))}
        </div>
        {styleDescription && <p className={lightDesc}>{styleDescription}</p>}
      </div>
      {/* Body: anchor image + stats */}
      <div className={lightBody}>
        <div className={lightAnchorSection}>
          <CenteredImage src={imgSrc} width={LIGHT_IMG_W} height={LIGHT_IMG_H} />
          <div className={lightAnchorFooter}>
            {!isNil(stars) && (
              <StarRatings
                rating={stars}
                numberOfStars={5}
                starDimension={starDimension}
                starSpacing={starSpacing}
                starRatedColor={starGoldColor}
              />
            )}
            {!isNil(renderedValue) && <span className={lightAnchorValue}>{renderedValue as React.ReactNode}</span>}
          </div>
          <div className={lightAnchorCaption}>
            <span className={lightAnchorCaptionId}>{id}</span>
            <span className={lightAnchorCaptionName}>{description}</span>
          </div>
        </div>
        <div className={lightSectionDetails}>
          {columns && columns.length > 0 && (
            <div className={lightStats}>
              {columns.map((col, i) => {
                const colRendererFn = (Renderer as unknown as Record<string, (v: unknown) => unknown>)[col.renderer];
                const colRenderedValue = colRendererFn ? colRendererFn(col.value) : col.value;
                const decoratedValue = decorateTextByClasses(colRenderedValue as React.ReactNode, col.classes ?? []);
                return (
                  <div key={i} className={lightStatRow}>
                    <span className={lightStatLabel}>{col.title}:</span>
                    <span className={lightStatValue}>{decoratedValue}</span>
                  </div>
                );
              })}
            </div>
          )}
          {/* Supporting items */}
          {!isEmpty(children) && (
            <>
              <div className={lightItemsRow}>
                {(children ?? []).map((item, index) => (
                  <div key={item.id || index} className={collCardStyles.itemCard}>
                    <div className={collCardStyles.itemImageContainer}>
                      <CenteredImage src={item.imgSrc} width={ITEM_THUMB_SIZE} height={ITEM_THUMB_SIZE} />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

CollectionCardLight.calcCardWidth = () => COLLCARD_WIDTH + HORPADDING;
CollectionCardLight.calcCardHeight = () => COLLCARD_HEIGHT + BORDERWIDTH * 2;
